import {useMemo} from 'react'
import {Input} from '@/components/ui/input'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {InlineRow} from '@/components/shared/InlineRow'
import {useAppConfig} from '@/hooks/use-app-config'

const PREFIXES = ['vault:', 'env:', 'plain:'] as const
type Prefix = typeof PREFIXES[number]

const DEFAULT_PREFIX: Prefix = 'vault:'

export interface SecretReferenceInputProps {
    id?: string
    value: string
    onChange: (value: string) => void
    placeholder?: string
    disabled?: boolean
    'data-testid'?: string
}

function splitValue(value: string): {prefix: Prefix | ''; suffix: string} {
    if (!value) {
        return {prefix: '', suffix: ''}
    }
    for (const p of PREFIXES) {
        if (value.startsWith(p)) {
            return {prefix: p, suffix: value.slice(p.length)}
        }
    }
    return {prefix: '', suffix: value}
}

export function SecretReferenceInput({
    id,
    value,
    onChange,
    placeholder,
    disabled,
    'data-testid': dataTestId,
}: SecretReferenceInputProps) {
    const appConfig = useAppConfig()
    const allowPlain = appConfig.devMode

    const {prefix, suffix} = useMemo(() => splitValue(value), [value])

    const visiblePrefixes: Prefix[] = useMemo(
        () => allowPlain ? [...PREFIXES] : PREFIXES.filter(p => p !== 'plain:'),
        [allowPlain],
    )

    const effectivePrefix: Prefix = prefix || DEFAULT_PREFIX

    function handlePrefixChange(next: string): void {
        const nextPrefix = next as Prefix
        onChange(suffix ? nextPrefix + suffix : '')
    }

    function handleSuffixChange(nextSuffix: string): void {
        onChange(nextSuffix ? effectivePrefix + nextSuffix : '')
    }

    return (
        <InlineRow gap="sm">
            <Select
                value={effectivePrefix}
                onValueChange={handlePrefixChange}
                disabled={disabled}
            >
                <SelectTrigger
                    data-testid={dataTestId ? `${dataTestId}-prefix` : undefined}
                    className="w-32"
                >
                    <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                    {visiblePrefixes.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <Input
                id={id}
                value={suffix}
                onChange={(e) => handleSuffixChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                data-testid={dataTestId}
            />
        </InlineRow>
    )
}
